/**
 * VoiceList Component
 *
 * Display list of user's saved voices.
 *
 * Playback belongs to the parent, through onPlay/onStop. Do NOT add an
 * `<audio src={voice.audio_url}>` back here: on the desktop build the page is
 * served from file:// and every API response carries
 * Cross-Origin-Resource-Policy: same-origin, so a media element pointed at our
 * own endpoint gets its response discarded by the browser - the request even
 * succeeds server-side, which makes it look like a corrupt file. The audio has
 * to be fetched and played from a blob; voiceApi.loadVoiceAudio does that.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { IVoiceInfo } from '../../types/voice.types';
import { formatFileSize } from '@/constants/fileSize';
import { ensureArray } from '../../utils/apiNormalizers';
import { ConfirmDialog } from '../common/ConfirmDialog';

interface VoiceListProps {
  voices: IVoiceInfo[];
  loading?: boolean;
  onDelete?: (voiceId: string) => void;
  onPlay?: (voiceId: string) => void;
  onStop?: () => void;
  playingVoiceId?: string | null;
  selectedVoiceId?: string;
  onSelect?: (voiceId: string) => void;
}

/**
 * Play icon SVG component.
 */
const PlayIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M8 5v14l11-7z" />
  </svg>
);

/**
 * Stop icon SVG component.
 */
const StopIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="6" y="6" width="12" height="12" />
  </svg>
);

/**
 * Trash icon SVG component.
 */
const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

/**
 * Get language display name from code.
 */
const getLanguageName = (code: string): string => {
  const languageNames: Record<string, string> = {
    en: 'English',
    pt: 'Portuguese',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    ja: 'Japanese',
    ko: 'Korean',
    zh: 'Chinese',
    ru: 'Russian',
    ar: 'Arabic',
    hi: 'Hindi',
    nl: 'Dutch',
    pl: 'Polish',
    tr: 'Turkish',
    vi: 'Vietnamese',
    th: 'Thai',
    id: 'Indonesian',
    uk: 'Ukrainian',
    sv: 'Swedish',
    da: 'Danish',
    no: 'Norwegian',
    fi: 'Finnish',
    cs: 'Czech',
    el: 'Greek',
    he: 'Hebrew',
    hu: 'Hungarian',
    ro: 'Romanian',
  };
  return languageNames[code.toLowerCase()] || code.toUpperCase();
};

export const VoiceList: React.FC<VoiceListProps> = ({
  voices,
  loading = false,
  onDelete,
  onPlay,
  onStop,
  playingVoiceId,
  selectedVoiceId,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const requestDelete = useCallback((voiceId: string) => {
    setPendingDeleteId(voiceId);
    setConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteId && onDelete) {
      onDelete(pendingDeleteId);
    }
    setConfirmOpen(false);
    setPendingDeleteId(null);
  }, [pendingDeleteId, onDelete]);

  const handleCancelDelete = useCallback(() => {
    setConfirmOpen(false);
    setPendingDeleteId(null);
  }, []);

  /**
   * Format date for display.
   */
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, []);

  if (loading) {
    return (
      <div className="voice-list p-4 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" />
        <p className="text-gray-500 mt-2">{t('voiceClone.loading')}</p>
      </div>
    );
  }

  // Defensive check: use centralized normalizer for array safety
  const safeVoices = ensureArray(voices);

  if (safeVoices.length === 0) {
    return (
      <div className="voice-list p-8 text-center bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-4xl mb-4">🎤</div>
        <p className="text-gray-500">{t('voiceClone.noVoices')}</p>
        <p className="text-sm text-gray-400 mt-2">{t('voiceClone.noVoicesHint')}</p>
      </div>
    );
  }

  return (
    <div className="voice-list space-y-3">
      {safeVoices.map((voice) => (
        <div
          key={voice.voice_id}
          className={`
            p-4 rounded-lg border transition-all cursor-pointer
            ${
              selectedVoiceId === voice.voice_id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }
          `}
          onClick={() => onSelect?.(voice.voice_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-gray-800 dark:text-gray-200">{voice.name}</h4>
                {/* Language badge - prioritize detected language over hint */}
                {voice.detected_language && (
                  <span
                    className={`
                      inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                      ${
                        voice.detection_confidence && voice.detection_confidence >= 0.85
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : voice.detection_confidence && voice.detection_confidence >= 0.7
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }
                    `}
                    title={
                      voice.detection_confidence
                        ? `Detected: ${getLanguageName(voice.detected_language)} (${Math.round(voice.detection_confidence * 100)}% confidence)`
                        : `Language: ${getLanguageName(voice.detected_language)}`
                    }
                  >
                    {getLanguageName(voice.detected_language)}
                  </span>
                )}
                {!voice.detected_language && voice.language_hint && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    title={`Hint: ${getLanguageName(voice.language_hint)}`}
                  >
                    {getLanguageName(voice.language_hint)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                <span>{voice.duration_seconds.toFixed(1)}s</span>
                <span>-</span>
                <span>{formatFileSize(voice.file_size_bytes)}</span>
                <span>-</span>
                <span>{formatDate(voice.created_at)}</span>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {/* Play button */}
              {voice.audio_url && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlay?.(voice.voice_id);
                  }}
                  disabled={playingVoiceId === voice.voice_id}
                  className={`p-2 rounded transition-colors ${
                    playingVoiceId === voice.voice_id
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                  }`}
                  title={t('voiceClone.play')}
                >
                  <PlayIcon />
                </button>
              )}

              {/* Stop button */}
              {voice.audio_url && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStop?.();
                  }}
                  disabled={playingVoiceId !== voice.voice_id}
                  className={`p-2 rounded transition-colors ${
                    playingVoiceId === voice.voice_id
                      ? 'text-orange-500 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                      : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  }`}
                  title={t('voiceClone.stop')}
                >
                  <StopIcon />
                </button>
              )}

              {/* Delete button */}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    requestDelete(voice.voice_id);
                  }}
                  className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors min-h-[44px] min-w-[44px]"
                  title={t('voiceClone.delete')}
                >
                  <TrashIcon />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      <ConfirmDialog
        isOpen={confirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title={t('voiceClone.deleteTitle', { defaultValue: 'Delete Voice' })}
        message={t('voiceClone.confirmDelete')}
        confirmText={t('common.delete', { defaultValue: 'Delete' })}
        cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
        variant="danger"
      />
    </div>
  );
};

export default VoiceList;
